import assertIsNever from '@app/utils/assertIsNever';

import {
    type FileUpload,
    type FileUploadStaged,
    isFileUploadError,
    isFileUploadStaged,
    isFileUploadUploading,
} from '../../model/fileUpload';

export const updateTargetFileUploadByFile = (
    fileUploads: FileUpload[],
    target: File,
    update: (target: FileUpload) => FileUpload,
) => fileUploads.map((fileUpload) => (fileUpload.file === target ? update(fileUpload) : fileUpload));

export const toRemoved = (upload: FileUpload): FileUpload => {
    switch (upload.state) {
        case 'success':
            return {
                state: 'removing',
                file: upload.file,
                uploadId: upload.uploadId,
                styleGuideId: upload.styleGuideId,
            };

        case 'staged':
            return {
                state: 'removing',
                file: upload.file,
                uploadId: upload.uploadId,
                styleGuideId: upload.styleGuideId,
                name: upload.name,
            };

        case 'pending':
        case 'uploading':
        case 'error':
        case 'removed':
            return { state: 'removing', file: upload.file, uploadId: upload.uploadId };

        case 'removing':
            return upload;

        default:
            return assertIsNever(upload);
    }
};

export const updateTargetFileUpload = (
    fileUploads: FileUpload[],
    target: FileUpload,
    update: (target: FileUpload) => FileUpload,
) => fileUploads.map((fileUpload) => (fileUpload === target ? update(fileUpload) : fileUpload));

export const updateProgress =
    (progressPercent: number) =>
    (fileUpload: FileUpload): FileUpload =>
        isFileUploadUploading(fileUpload)
            ? { ...fileUpload, progressPercent }
            : { file: fileUpload.file, uploadId: fileUpload.uploadId, state: 'uploading', progressPercent };

export const updateError =
    (error: string, retry?: () => void) =>
    (fileUpload: FileUpload): FileUpload =>
        isFileUploadError(fileUpload)
            ? { ...fileUpload, error, retry }
            : { file: fileUpload.file, uploadId: fileUpload.uploadId, state: 'error', error, retry };

export const updateSuccess =
    (styleGuideId: string) =>
    (fileUpload: FileUpload): FileUpload => ({
        file: fileUpload.file,
        uploadId: fileUpload.uploadId,
        state: 'success',
        styleGuideId,
    });

export const updateStaged =
    (update: Partial<FileUploadStaged>) =>
    (fileUpload: FileUpload): FileUpload =>
        isFileUploadStaged(fileUpload) ? { ...fileUpload, ...update } : fileUpload;
